import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building2, Save, Plus, Lock, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Password Change Component
const PasswordChangeCard = () => {
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/users/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-orbit-card border-orbit-border">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Lock className="w-5 h-5 text-primary" />
          Change Password
        </CardTitle>
        <CardDescription>
          Update your account password. Make sure to use a strong password.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div className="space-y-2">
            <Label>Current Password *</Label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Enter current password"
                required
                data-testid="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>New Password *</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="Enter new password"
                  required
                  data-testid="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password *</Label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <Button type="submit" className="bg-orbit-glow hover:opacity-90" disabled={loading} data-testid="change-password-btn">
            <Lock className="w-4 h-4 mr-2" />
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

const Settings = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    registration_number: '',
    address: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Malaysia',
    phone: '',
    email: '',
    lhdn: {
      tin: '',
      sst_id: '',
      msic_code: '',
      business_activity: ''
    }
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
      if (response.data.length > 0) {
        // Pre-fill form with first company data
        const company = response.data[0];
        setCompanyForm({
          ...company,
          lhdn: company.lhdn || {
            tin: '',
            sst_id: '',
            msic_code: '',
            business_activity: ''
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch companies');
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/companies`, companyForm);
      toast.success('Company saved successfully!');
      fetchCompanies();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save company';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white">Settings</h1>
        <p className="text-muted-foreground">Manage your company and account settings</p>
      </div>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="bg-orbit-card border border-orbit-border">
          <TabsTrigger value="company">Company</TabsTrigger>
          <TabsTrigger value="compliance">LHDN Compliance</TabsTrigger>
          <TabsTrigger value="account">Account</TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Company Information
                </CardTitle>
                <CardDescription>
                  This information will appear on your invoices and documents.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCompanySubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Company Name *</Label>
                      <Input
                        value={companyForm.name}
                        onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                        placeholder="Acme Sdn Bhd"
                        required
                        data-testid="company-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Registration Number *</Label>
                      <Input
                        value={companyForm.registration_number}
                        onChange={(e) => setCompanyForm({ ...companyForm, registration_number: e.target.value })}
                        placeholder="123456-X"
                        required
                        data-testid="company-reg"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Address *</Label>
                    <Textarea
                      value={companyForm.address}
                      onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                      placeholder="123 Jalan Ampang"
                      required
                      data-testid="company-address"
                    />
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>City *</Label>
                      <Input
                        value={companyForm.city}
                        onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                        placeholder="Kuala Lumpur"
                        required
                        data-testid="company-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State *</Label>
                      <Input
                        value={companyForm.state}
                        onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })}
                        placeholder="Wilayah Persekutuan"
                        required
                        data-testid="company-state"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Postcode *</Label>
                      <Input
                        value={companyForm.postcode}
                        onChange={(e) => setCompanyForm({ ...companyForm, postcode: e.target.value })}
                        placeholder="50450"
                        required
                        data-testid="company-postcode"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone *</Label>
                      <Input
                        value={companyForm.phone}
                        onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                        placeholder="+60 3-1234 5678"
                        required
                        data-testid="company-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={companyForm.email}
                        onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                        placeholder="billing@acme.com"
                        required
                        data-testid="company-email"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="bg-orbit-glow hover:opacity-90" disabled={loading} data-testid="save-company">
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Company'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* LHDN Compliance */}
        <TabsContent value="compliance">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader>
                <CardTitle className="text-white">LHDN E-Invoice Compliance</CardTitle>
                <CardDescription>
                  Configure your Malaysia tax compliance settings for e-Invoice submission.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCompanySubmit} className="space-y-6">
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 mb-6">
                    <p className="text-sm text-primary">
                      These details are required for LHDN MyInvois e-Invoice compliance. 
                      Ensure all information matches your official tax registration.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>TIN (Tax Identification Number) *</Label>
                      <Input
                        value={companyForm.lhdn.tin}
                        onChange={(e) => setCompanyForm({
                          ...companyForm,
                          lhdn: { ...companyForm.lhdn, tin: e.target.value }
                        })}
                        placeholder="C12345678901"
                        data-testid="lhdn-tin"
                      />
                      <p className="text-xs text-muted-foreground">Your company's Tax Identification Number</p>
                    </div>
                    <div className="space-y-2">
                      <Label>SST Registration ID</Label>
                      <Input
                        value={companyForm.lhdn.sst_id}
                        onChange={(e) => setCompanyForm({
                          ...companyForm,
                          lhdn: { ...companyForm.lhdn, sst_id: e.target.value }
                        })}
                        placeholder="W12-1234-56789012"
                        data-testid="lhdn-sst"
                      />
                      <p className="text-xs text-muted-foreground">Sales and Service Tax registration (if applicable)</p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>MSIC Code</Label>
                      <Input
                        value={companyForm.lhdn.msic_code}
                        onChange={(e) => setCompanyForm({
                          ...companyForm,
                          lhdn: { ...companyForm.lhdn, msic_code: e.target.value }
                        })}
                        placeholder="62010"
                        data-testid="lhdn-msic"
                      />
                      <p className="text-xs text-muted-foreground">Malaysian Standard Industrial Classification code</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Business Activity Description</Label>
                      <Input
                        value={companyForm.lhdn.business_activity}
                        onChange={(e) => setCompanyForm({
                          ...companyForm,
                          lhdn: { ...companyForm.lhdn, business_activity: e.target.value }
                        })}
                        placeholder="Software Development"
                        data-testid="lhdn-activity"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="bg-orbit-glow hover:opacity-90" disabled={loading} data-testid="save-compliance">
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Compliance Settings'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Account Settings */}
        <TabsContent value="account">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <Card className="bg-orbit-card border-orbit-border">
              <CardHeader>
                <CardTitle className="text-white">Account Information</CardTitle>
                <CardDescription>
                  Your personal account details.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input value={user?.full_name || ''} disabled />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input value={user?.email || ''} disabled />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Input value={user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || ''} disabled />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input value={user?.phone || '-'} disabled />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Password Change Card */}
            <PasswordChangeCard />
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
